import * as React from 'react';
import clsx from 'clsx';
import { InputProps } from 'interfaces';
import { ErrorMessageInput, Icon } from 'components/common';
import { CypressI } from 'interfaces/cypress';
import { TooltipComponent } from '@/components/common/tooltip';
import Icons from '@/const/icons';

export const Input = React.forwardRef<
	HTMLInputElement,
	InputProps & CypressI & React.InputHTMLAttributes<HTMLInputElement>
>(
	(
		{
			id,
			dataCY,
			name,
			title,
			register,
			rules,
			iconRight,
			iconLeft,
			rightClick,
			leftClick,
			customPlaceholder,
			onChangeCustom,
			error,
			className,
			classNameTitle,
			classNameInput,
			classNameIcon,
			colorIcon,
			markAsRequired,
			tooltipLabel,
			withOutErrorIcon,
			additionalInfoTitle,
			showCount,
			...props
		},
		ref
	) => {
		const registerAux = register(name, rules);
		const [charCount, setCharCount] = React.useState(
			() => String(props.value || props.defaultValue || '').length
		);

		React.useEffect(() => {
			if (props.value !== undefined) {
				setCharCount(String(props.value).length);
			}
		}, [props.value]);

		return (
			<div className={clsx('relative flex flex-col', className)}>
				<div className="relative group">
					<label
						htmlFor={id || name}
						className={clsx(
							'block text-base text-left',
							error ? 'text-status-error-solid' : 'text-black',
							classNameTitle
						)}
					>
						<span className="relative">
							{title}
							{markAsRequired && (
								// <Asterisk className="w-3 h-3 absolute -right-3 top-0 text-red-500" />
								<span className="text-main-red absolute -right-3 top-0">*</span>
							)}
							{additionalInfoTitle && (
								<span className="text-opacity-black">
									{' '}
									{additionalInfoTitle}
								</span>
							)}
						</span>
					</label>
					<input
						id={id || name}
						data-cy={dataCY}
						name={name}
						placeholder={customPlaceholder}
						autoComplete={props.autoComplete || 'off'}
						className={clsx(
							{
								'text-main-red border-main-red': error,
							},
							{ 'px-6': !iconLeft && !iconRight },
							{ 'pl-14 pr-4': iconLeft },
							{ 'pl-6 pr-10': iconRight },
							'mt-2 placeholder-opacity-primary  placeholder:text-base py-3 w-full text-base font-sans border rounded-full bg-white',
							'focus:outline-none focus:ring-offset-transparent',
							'disabled:placeholder-dark-40 disabled:cursor-not-allowed disabled:text-opacity-primary group-hover:disabled:bg-[#F4F4F4A3]',
							{
								'focus:ring-black text-black border-opacity-primary': !error,
							},
							classNameInput
						)}
						ref={(el) => {
							if (registerAux?.ref && typeof registerAux.ref === 'function') {
								registerAux.ref(el);
							}
							if (ref && typeof ref === 'function') ref(el);
							else if (ref && typeof ref === 'object') ref.current = el;
						}}
						onChange={(e) => {
							registerAux && registerAux.onChange(e);
							onChangeCustom && onChangeCustom(e);
							setCharCount(e.target.value.length);
						}}
						{...props}
					/>
					{iconLeft && (
						<div
							onClick={leftClick}
							className={clsx(
								'absolute left-4 top-[55%] w-6 h-6',
								classNameIcon
							)}
						>
							<Icon
								src={iconLeft}
								className={clsx('cursor-pointer', colorIcon)}
							/>
						</div>
					)}
					{iconRight && (
						<>
							<TooltipComponent
								className="w-56"
								align="end"
								label={tooltipLabel}
								trigger={
									<button
										type="button"
										aria-label={
											iconRight === Icons.calendar
												? 'Open date picker'
												: undefined
										}
										onClick={rightClick}
										className={clsx(
											'absolute right-4 top-[55%] w-6 h-6',
											classNameIcon
										)}
									>
										<Icon
											src={iconRight}
											className={clsx('cursor-pointer', colorIcon)}
										/>
									</button>
								}
							/>
						</>
					)}
				</div>
				{(showCount || (error && error.message && !props.hideErrorMessage)) && (
					<div className="flex justify-between items-start mt-1 px-4">
						<div className="flex-grow">
							{error && error.message && !props.hideErrorMessage && (
								<ErrorMessageInput
									message={error.message}
									withOutIcon={withOutErrorIcon}
								/>
							)}
						</div>
						{showCount && props.maxLength && (
							<span className="text-xs text-opacity-black font-sans ml-auto mt-1 whitespace-nowrap">
								{charCount}
								{`/${props.maxLength}`}
							</span>
						)}
					</div>
				)}
			</div>
		);
	}
);

Input.displayName = 'Input';
